import React from "react";
import { Tooltip, IconButton, IconButtonProps } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useTranslation } from "react-i18next";

/**
 * Wee had to omit the onClick prop from the IconButtonProps because
 * we're handling the onClick functionality ourselves and we don't want to
 * we don't want to override it with users passing random onClick functionality.
 */
interface CopyIconButtonProps extends Omit<IconButtonProps, "onClick"> {
  textToCopy: string;
  tooltip?: string;
  copiedTooltip?: string;
  onCopyComplete?: () => void;
  testId?: string;
}

const CopyIconButton = ({
  textToCopy,
  tooltip,
  copiedTooltip,
  onCopyComplete,
  testId,
  disabled,
  sx,
  ...iconButtonProps
}: CopyIconButtonProps) => {
  const { copyState, copy, isPending } = useCopyToClipboard();
  const { t } = useTranslation();

  const defaultTooltip = tooltip || t("general.copyToClipboard");
  const defaultCopiedTooltip = copiedTooltip || t("general.copied");

  const hasValidText = textToCopy && textToCopy.trim() !== "";
  if (!hasValidText) {
    return null;
  }

  const handleCopy = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    copy(textToCopy);
    onCopyComplete?.();
  };

  const tooltipText = copyState === "copied" ? defaultCopiedTooltip : defaultTooltip;

  return (
    <Tooltip title={tooltipText}>
      <IconButton
        size="small"
        aria-label={defaultTooltip}
        disabled={disabled || isPending}
        onClick={handleCopy}
        loading={isPending}
        data-testid={testId}
        sx={{
          ml: 1,
          color: copyState === "copied" ? "success.main" : ((theme) => theme.palette.grey[500]),
          transition: "color 150ms ease",
          "&:hover": {
            color: copyState === "copied" ? "success.main" : ((theme) => theme.palette.grey[600]),
          },
          ...sx,
        }}
        {...iconButtonProps}
      >
        {copyState === "copied" ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
};

export default CopyIconButton;